import { Building, User2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { LivePulse } from "@/components/estate";
import { cn } from "@/lib/utils";
import { formatTime } from "@/lib/date";
import { AppointmentStatusBadge } from "@/features/appointment/components/appointment-status-badge";
import {
  NOMINAL_VIEWING_MINUTES,
  type Appointment,
} from "@/features/appointment/types";

function isAppointmentActive(appointment: Appointment): boolean {
  if (appointment.status !== "SCHEDULED") return false;
  const start = new Date(appointment.scheduledAt).getTime();
  const end = start + NOMINAL_VIEWING_MINUTES * 60_000;
  const now = Date.now();
  return now >= start && now <= end;
}

export interface AppointmentCardLeadView {
  fullName: string | null;
}

export interface AppointmentCardPropertyView {
  buildingName: string | null;
}

export interface AppointmentCardBrokerView {
  fullName: string | null;
}

interface AppointmentCardProps {
  appointment: Appointment;
  lead?: AppointmentCardLeadView | undefined;
  property?: AppointmentCardPropertyView | undefined;
  broker?: AppointmentCardBrokerView | undefined;
  onClick?: ((appointment: Appointment) => void) | undefined;
  /** Compact = week-view column; default = agenda row. */
  compact?: boolean | undefined;
}

export function AppointmentCard({
  appointment,
  lead,
  property,
  broker,
  onClick,
  compact,
}: AppointmentCardProps) {
  const handleClick = () => onClick?.(appointment);
  const handleKey = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClick?.(appointment);
    }
  };

  // A redacted row (a colleague's viewing, viewableInFull=false) has its
  // lead/property nulled by the backend; show the broker as the subject and
  // never surface property / notes for it (defense-in-depth on top of the API).
  const redacted = !appointment.viewableInFull;
  const subjectName = redacted
    ? broker?.fullName ?? "Lịch hẹn"
    : lead?.fullName ?? "Khách hàng";
  const propertyName = redacted ? null : property?.buildingName ?? null;

  return (
    <Card
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : -1}
      onClick={onClick ? handleClick : undefined}
      onKeyDown={onClick ? handleKey : undefined}
      aria-label={onClick ? `Xem lịch hẹn lúc ${formatTime(appointment.scheduledAt)} với ${subjectName}` : undefined}
      className={cn(
        "flex flex-col gap-1.5 rounded-lg p-3 transition-shadow",
        compact ? "gap-1 p-2 text-xs" : "min-h-[88px]",
        onClick
          ? "cursor-pointer hover:shadow-[var(--shadow-card)] focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
          : "",
      )}
      data-testid={`appointment-card-${appointment.id}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span
          className={cn(
            "inline-flex items-center gap-2 font-mono font-medium tabular-nums",
            compact ? "text-xs" : "text-sm",
          )}
        >
          {isAppointmentActive(appointment) ? <LivePulse tone="success" /> : null}
          {formatTime(appointment.scheduledAt)}
        </span>
        <AppointmentStatusBadge
          status={appointment.status}
          className={compact ? "text-[11px]" : ""}
        />
      </div>

      <p
        className={cn(
          "truncate font-semibold",
          compact ? "text-xs" : "text-sm",
        )}
      >
        {subjectName}
        {propertyName ? (
          <>
            <span className="mx-1 text-muted-foreground">·</span>
            <span className="text-muted-foreground">{propertyName}</span>
          </>
        ) : null}
      </p>

      {!compact && !redacted ? (
        <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
          {broker?.fullName ? (
            <span className="inline-flex items-center gap-1 truncate">
              <User2 aria-hidden="true" className="size-3" />
              <span className="truncate">Đại diện: {broker.fullName}</span>
            </span>
          ) : null}
          {appointment.notes ? (
            <span className="inline-flex items-center gap-1 truncate">
              <Building aria-hidden="true" className="size-3" />
              <span className="max-w-[16ch] truncate">{appointment.notes}</span>
            </span>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}
